import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Building2, ExternalLink, Check } from 'lucide-react';

const Register = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [role, setRole] = useState('staff');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { register } = useAuth();
  const navigate = useNavigate();

  // Consent checkboxes
  const [gdprConsent, setGdprConsent] = useState(false);
  const [termsConsent, setTermsConsent] = useState(false);
  const [likenessConsent, setLikenessConsent] = useState(false);
  const [ndaConsent, setNdaConsent] = useState(false);

  const isNonOwner = role !== 'owner';
  const allConsentsGiven = gdprConsent && termsConsent && likenessConsent && ndaConsent;
  const canSubmit = !isNonOwner || allConsentsGiven;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (isNonOwner && !allConsentsGiven) {
      setError('You must accept all legal agreements to register.');
      return;
    }

    setLoading(true);
    try {
      const payload = {
        email,
        password,
        name,
        role
      };
      if (isNonOwner) {
        payload.consents = {
          gdpr_consent: gdprConsent,
          terms_consent: termsConsent,
          likeness_consent: likenessConsent,
          nda_consent: ndaConsent
        };
      }

      const API_URL = process.env.REACT_APP_BACKEND_URL;
      const { data } = await (await import('axios')).default.post(
        `${API_URL}/api/auth/register`,
        payload,
        { withCredentials: true }
      );
      navigate('/dashboard');
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (typeof detail === 'string') {
        setError(detail);
      } else if (Array.isArray(detail)) {
        setError(detail.map(e => e.msg || JSON.stringify(e)).join(' '));
      } else {
        setError('Registration failed. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const ConsentCheckbox = ({ checked, onChange, label, linkTo, linkLabel, testId }) => (
    <label className="flex items-start gap-3 cursor-pointer group" data-testid={testId}>
      <div className="relative mt-0.5 flex-shrink-0">
        <input
          type="checkbox"
          checked={checked}
          onChange={(e) => onChange(e.target.checked)}
          className="sr-only"
        />
        <div
          className="w-5 h-5 rounded border-2 flex items-center justify-center transition-all duration-200"
          style={{
            borderColor: checked ? '#D4AF37' : '#CBD5E1',
            backgroundColor: checked ? '#D4AF37' : 'transparent'
          }}
        >
          {checked && <Check className="w-3 h-3 text-white" />}
        </div>
      </div>
      <span className="text-sm leading-relaxed" style={{ color: '#4A4033' }}>
        {label}{' '}
        {linkTo && (
          <Link
            to={linkTo}
            target="_blank"
            className="inline-flex items-center gap-1 font-medium underline transition-colors duration-200"
            style={{ color: '#D4AF37' }}
            data-testid={`${testId}-link`}
          >
            {linkLabel}
            <ExternalLink className="w-3 h-3" />
          </Link>
        )}
      </span>
    </label>
  );

  return (
    <div
      className="min-h-screen flex items-center justify-center bg-cover bg-center py-8"
      style={{
        backgroundImage: 'url(https://images.pexels.com/photos/4965823/pexels-photo-4965823.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940)',
        backgroundColor: 'rgba(0,0,0,0.5)',
        backgroundBlendMode: 'darken'
      }}
    >
      <div className="bg-white p-8 rounded-sm shadow-sm w-full max-w-md border" style={{ borderColor: '#E5E1DB' }}>
        <div className="flex items-center justify-center mb-6">
          <div className="w-10 h-10 rounded-full flex items-center justify-center mr-3" style={{ backgroundColor: '#D4AF37' }}>
            <Building2 className="w-6 h-6 text-white" />
          </div>
          <h1 className="text-2xl font-light tracking-wide" style={{ color: '#2C2416' }}>Operations Suite</h1>
        </div>

        <h2 className="text-lg font-medium mb-5 text-center" style={{ color: '#8B7355' }}>Create your account</h2>

        {error && (
          <div className="mb-4 p-3 rounded-sm text-sm" style={{ backgroundColor: '#FEF2F2', border: '1px solid #FECACA', color: '#991B1B' }} data-testid="register-error">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} data-testid="register-form">
          <div className="mb-4">
            <label className="block text-sm font-medium mb-1.5" style={{ color: '#2C2416' }}>Full Name</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full px-4 py-2.5 border rounded-sm focus:outline-none focus:ring-2 transition-all duration-200"
              style={{ borderColor: '#E5E1DB', focusRingColor: '#D4AF37' }}
              required
              data-testid="register-name-input"
            />
          </div>

          <div className="mb-4">
            <label className="block text-sm font-medium mb-1.5" style={{ color: '#2C2416' }}>Email</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full px-4 py-2.5 border rounded-sm focus:outline-none focus:ring-2 transition-all duration-200"
              style={{ borderColor: '#E5E1DB' }}
              required
              data-testid="register-email-input"
            />
          </div>

          <div className="mb-4">
            <label className="block text-sm font-medium mb-1.5" style={{ color: '#2C2416' }}>Password</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full px-4 py-2.5 border rounded-sm focus:outline-none focus:ring-2 transition-all duration-200"
              style={{ borderColor: '#E5E1DB' }}
              required
              minLength="6"
              data-testid="register-password-input"
            />
          </div>

          <div className="mb-5">
            <label className="block text-sm font-medium mb-1.5" style={{ color: '#2C2416' }}>Role</label>
            <select
              value={role}
              onChange={(e) => setRole(e.target.value)}
              className="w-full px-4 py-2.5 border rounded-sm focus:outline-none focus:ring-2 transition-all duration-200"
              style={{ borderColor: '#E5E1DB' }}
              data-testid="register-role-select"
            >
              <option value="staff">Staff</option>
              <option value="accountant">Accountant</option>
              <option value="investor">Investor</option>
              <option value="owner">Owner</option>
            </select>
          </div>

          {/* Legal Consents - Only for non-owner roles */}
          {isNonOwner && (
            <div className="mb-5 p-4 rounded-sm" style={{ backgroundColor: '#FDFCFB', border: '1px solid #E5E1DB' }} data-testid="legal-consents-section">
              <h3 className="text-sm font-semibold uppercase tracking-widest mb-3" style={{ color: '#8B7355' }}>
                Legal Agreements Required
              </h3>
              <div className="space-y-3">
                <ConsentCheckbox
                  checked={gdprConsent}
                  onChange={setGdprConsent}
                  label="I consent to the processing of my personal data in accordance with the"
                  linkTo="/legal/privacy-policy"
                  linkLabel="Privacy Policy (GDPR)"
                  testId="consent-gdpr"
                />
                <ConsentCheckbox
                  checked={termsConsent}
                  onChange={setTermsConsent}
                  label="I have read and agree to the"
                  linkTo="/legal/terms"
                  linkLabel="Terms of Service"
                  testId="consent-terms"
                />
                <ConsentCheckbox
                  checked={likenessConsent}
                  onChange={setLikenessConsent}
                  label="I consent to my name and professional likeness being used in internal company materials"
                  testId="consent-likeness"
                />
                <ConsentCheckbox
                  checked={ndaConsent}
                  onChange={setNdaConsent}
                  label="I have read and agree to the"
                  linkTo="/legal/nda"
                  linkLabel="Universal Non-Disclosure Agreement"
                  testId="consent-nda"
                />
              </div>
            </div>
          )}

          <button
            type="submit"
            disabled={loading || !canSubmit}
            className="w-full py-3 rounded-sm font-medium transition-all duration-200 disabled:opacity-40"
            style={{
              backgroundColor: canSubmit ? '#D4AF37' : '#CBD5E1',
              color: canSubmit ? 'white' : '#94A3B8'
            }}
            data-testid="register-submit-button"
          >
            {loading ? 'Creating account...' : 'Create Account'}
          </button>
        </form>

        <p className="mt-5 text-center text-sm" style={{ color: '#8B7355' }}>
          Already have an account?{' '}
          <Link to="/login" className="font-medium underline" style={{ color: '#D4AF37' }} data-testid="login-link">
            Sign in
          </Link>
        </p>
      </div>
    </div>
  );
};

export default Register;
